import {
  Box,
  Button,
  Divider,
  Grid,
  Select,
  MenuItem,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useToaster } from 'src/utils/toaster/toasterContext';
import HttpServices from 'src/services/httpService';
import { crmModuleURL } from 'src/services/urlService';
import SendIcon from '@mui/icons-material/Send';
import DMTemplateConfiguration from './DMTemplateConfiguration';
import { useTranslation } from 'react-i18next';
import { CommonHelper } from 'src/utils/commonHelper';

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '0.875rem',
  display: 'flex',
  alignItems: 'center',
  padding: '6px 12px',
  maxWidth: '100%',
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.light,
    '&:hover': { backgroundColor: theme.palette.primary.light },
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  fontSize: '0.875rem',
  bgcolor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  '& .MuiSelect-select': {
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
  },
  maxWidth: '100%',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    fontSize: '0.875rem',
    bgcolor: theme.palette.background.paper,
    borderRadius: 1,
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
}));

const TriggerConfiguration = ({ selectedProfile, selectedPost, posts, onPostSelect, isMobile }) => {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const [triggerType, setTriggerType] = useState('comment');
  const [dmMessage, setDmMessage] = useState('');
  const [commentReply, setCommentReply] = useState('');
  const [triggerValues, setTriggerValues] = useState([]);
  const [inputTriggerValue, setInputTriggerValue] = useState('');
  const [useTemplate, setUseTemplate] = useState(false);
  const [allPost, setAllPost] = useState(false);
  const [anyTriggerValue, setAnyTriggerValue] = useState(false);
  const [template, setTemplate] = useState({
    title: '',
    subtitle: '',
    image_url: '',
    default_action: { type: 'web_url', url: '' },
    buttons: [],
  });

  const handleTriggerTypeChange = (event) => {
    setTriggerType(event.target.value);
    setDmMessage('');
    setCommentReply('');
    setTriggerValues([]);
    setInputTriggerValue('');
    setAnyTriggerValue(false);
  };

  const handleTemplateChange = (newTemplate) => {
    setTemplate(newTemplate);
  };

  const handleAddTriggerValue = () => {
    if (inputTriggerValue.trim()) {
      setTriggerValues([...triggerValues, inputTriggerValue.trim()]);
      setInputTriggerValue('');
    }
  };

  const handleDeleteTriggerValue = (valueToDelete) => {
    setTriggerValues(triggerValues.filter((value) => value !== valueToDelete));
  };

  const handleSaveTrigger = () => {
    if (!selectedProfile) {
      showToast(translate('trigger_configuration.please_select_profile', 'Please select a profile'), 'e');
      return;
    }
    if (!triggerType) {
      showToast(translate('trigger_configuration.please_select_trigger_type', 'Please select a trigger type'), 'e');
      return;
    }
    if (!allPost && !selectedPost) {
      showToast(translate('trigger_configuration.please_select_post', 'Please select a post when not applying to all posts'), 'e');
      return;
    }
    if (!anyTriggerValue && triggerType !== 'like' && triggerValues.length === 0 && !inputTriggerValue.trim()) {
      showToast(translate('trigger_configuration.at_least_one_trigger_value', 'At least one trigger value is required unless any trigger value is enabled'), 'e');
      return;
    }

    if (!anyTriggerValue && triggerType !== 'like' && triggerValues.length === 0 && inputTriggerValue.trim()) {
      showToast(translate('trigger_configuration.press_enter_to_add_trigger_value', 'Press Enter to add the trigger value to the list'), 'e');
      return;
    }
    if (useTemplate) {
      if (!template.title.trim()) {
        showToast(translate('trigger_configuration.template_title_required', 'Template title is required'), 'e');
        return;
      }
      if (template.buttons.length > 3) {
        showToast(translate('trigger_configuration.max_3_buttons', 'Maximum 3 buttons allowed in template'), 'e');
        return;
      }
    } else {
      if (triggerType === 'like' && !dmMessage.trim()) {
        showToast(translate('trigger_configuration.dm_message_required_like', 'DM message is required for like trigger'), 'e');
        return;
      }
      if (triggerType === 'comment' && !dmMessage.trim() && !commentReply.trim()) {
        showToast(translate('trigger_configuration.at_least_one_dm_or_comment', 'At least one of DM message or comment reply is required for comment trigger'), 'e');
        return;
      }
    }
    const triggerData = {
      webhook_profile_id: selectedProfile.webhook_profile_id,
      post_id: allPost ? '' : selectedPost?.id || '',
      service_name: selectedProfile?.service_name,
      trigger_type: triggerType,
      trigger_values: anyTriggerValue ? [] : triggerValues,
      dm_message: useTemplate ? '' : dmMessage,
      comment_reply: triggerType === 'comment' ? commentReply : '',
      dm_template: useTemplate ? template : null,
      is_dm_template: useTemplate,
      allPost: allPost,
      any_trigger_value: anyTriggerValue,
    };

    HttpServices.Post({
      url: crmModuleURL.saveAutomationTrigger,
      body: triggerData,
      successCallback: (data, message) => {
        CommonHelper.EnsureConversionFactorFlag(CommonHelper.ConversionFlagList.FIRST_AUTO_DM_STARTED)
        showToast(message || translate('trigger_configuration.automation_trigger_saved', 'Automation trigger saved successfully'), 's');
        setTriggerType('');
        setDmMessage('');
        setCommentReply('');
        setTriggerValues([]);
        setInputTriggerValue('');
        setUseTemplate(false);
        setAllPost(true);
        setAnyTriggerValue(false);
        setTemplate({
          title: '',
          subtitle: '',
          image_url: '',
          default_action: { type: 'web_url', url: '' },
          buttons: [],
        });
        onPostSelect(null);
      },
      failureCallback: (message) => {
        showToast(message || translate('trigger_configuration.failed_to_save', 'Failed to save automation trigger'), 'e');
      },
    });
  };

  const getPostDescription = (post) => {
    const text = post.caption || post.message || `Post ${post.id}`;
    return text.length > 40 ? `${text.substring(0, 37)}...` : text;
  };

  const triggerOptions = [
    { value: 'comment', label: translate('trigger_configuration.comment_dm_reply', 'Comment (DM and/or Reply)') },
  ];

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        p: 3,
        borderRadius: 2,
        height: 'calc(100vh - 240px)',
        boxShadow: 3,
        overflowY: 'auto',
      }}
    >
      <Typography variant="h6" gutterBottom color="text.primary">
        {translate('trigger_configuration.configure_automation_trigger', 'Configure Automation Trigger')}
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StyledSelect
            size="small"
            value={triggerType}
            onChange={handleTriggerTypeChange}
            displayEmpty
            sx={{ width: '100%' }}
            renderValue={(selected) => {
              const selectedOption = triggerOptions.find((option) => option.value === selected);
              return selectedOption ? (
                <Typography sx={{ fontSize: '0.875rem' }}>{selectedOption.label}</Typography>
              ) : (
                <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  {translate('trigger_configuration.select_trigger_type', 'Select Trigger Type')}
                </Typography>
              );
            }}
          >
            {triggerOptions.map((option) => {
              if (option.value === 'like' && selectedProfile?.service_name !== 'facebook') return null;
              return (
                <StyledMenuItem key={option.value} value={option.value}>
                  {option.label}
                </StyledMenuItem>
              );
            })}
          </StyledSelect>
        </Grid>
        {triggerType && (
          <>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={allPost} onChange={(e) => setAllPost(e.target.checked)} color="primary" />}
                label={translate('trigger_configuration.apply_to_all_posts', 'Apply to All Posts')}
              />
            </Grid>
            {!allPost && (
              <Grid item xs={12}>
                <StyledSelect
                  size="small"
                  value={selectedPost?.id || ''}
                  onChange={(e) => onPostSelect(posts.find((p) => p.id === e.target.value))}
                  displayEmpty
                  sx={{ width: '100%' }}
                  renderValue={(selected) => {
                    return selected ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '100%' }}>
                        {(posts.find((post) => post.id === selected)?.image_url ||
                          posts.find((post) => post.id === selected)?.media_url) && (
                          <Box
                            component="img"
                            src={
                              posts.find((post) => post.id === selected)?.image_url ||
                              posts.find((post) => post.id === selected)?.media_url
                            }
                            alt={translate('trigger_configuration.selected_post_alt', 'Selected post')}
                            sx={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 1 }}
                          />
                        )}
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={getPostDescription(posts.find((post) => post.id === selected) || {})}
                        >
                          {getPostDescription(posts.find((post) => post.id === selected) || {})}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {translate('trigger_configuration.select_a_post', 'Select a Post')}
                      </Typography>
                    );
                  }}
                >
                  {posts.map((post) => (
                    <StyledMenuItem key={post.id} value={post.id}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          maxWidth: isMobile ? '250px' : '350px',
                          overflow: 'hidden',
                        }}
                      >
                        {(post.image_url || post.media_url) && (
                          <Box
                            component="img"
                            src={post.image_url || post.media_url}
                            alt={translate('trigger_configuration.post_preview_alt', 'Post preview')}
                            sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                            onError={(e) => (e.target.style.display = 'none')}
                          />
                        )}
                        <Box sx={{ overflow: 'hidden' }}>
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={getPostDescription(post)}
                          >
                            {getPostDescription(post)}
                          </Typography>
                          {post.created_time && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {new Date(post.created_time).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </StyledMenuItem>
                  ))}
                </StyledSelect>
              </Grid>
            )}
            {triggerType !== 'like' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={anyTriggerValue}
                      onChange={(e) => setAnyTriggerValue(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={translate('trigger_configuration.accept_any_trigger_value', 'Accept Any Trigger Value')}
                />
              </Grid>
            )}
            {triggerType !== 'like' && !anyTriggerValue && (
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label={translate('trigger_configuration.trigger_values', 'Trigger Values')}
                  value={inputTriggerValue}
                  onChange={(e) => setInputTriggerValue(e.target.value)}
                  placeholder={translate('trigger_configuration.enter_trigger_value', 'Enter trigger value and press Enter')}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTriggerValue()}
                  error={triggerValues.length === 0}
                  helperText={triggerValues.length === 0 ? translate('trigger_configuration.at_least_one_trigger_value_required', 'At least one trigger value required') : ''}
                />
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {triggerValues.map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      onDelete={() => handleDeleteTriggerValue(value)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={useTemplate} onChange={(e) => setUseTemplate(e.target.checked)} />}
                label={translate('trigger_configuration.use_generic_template_dm', 'Use Generic Template DM')}
                sx={{ mb: 1 }}
              />
            </Grid>
            {!useTemplate && (
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label={translate('trigger_configuration.dm_message', 'DM Message')}
                  value={dmMessage}
                  onChange={(e) => setDmMessage(e.target.value)}
                  placeholder={translate('trigger_configuration.enter_dm_message', 'Enter DM message to send')}
                  multiline
                  rows={2}
                  error={triggerType === 'like' && !dmMessage.trim()}
                  helperText={triggerType === 'like' && !dmMessage.trim() ? translate('trigger_configuration.required', 'Required') : ''}
                />
              </Grid>
            )}
            {useTemplate && (
              <Grid item xs={12}>
                <DMTemplateConfiguration template={template} onChange={handleTemplateChange} />
              </Grid>
            )}
            {triggerType === 'comment' && (
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label={translate('trigger_configuration.comment_reply_optional', 'Comment Reply (Optional)')}
                  value={commentReply}
                  onChange={(e) => setCommentReply(e.target.value)}
                  placeholder={translate('trigger_configuration.enter_comment_reply', 'Enter comment reply')}
                  multiline
                  rows={2}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveTrigger}
                startIcon={<SendIcon />}
                sx={{ mt: 2, borderRadius: 1 }}
              >
                {translate('trigger_configuration.save_trigger', 'Save Trigger')}
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default TriggerConfiguration;
